import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight, Home as HomeIcon, Building2, Paintbrush, Star, Play } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { portfolioImages, portfolioProjects, stats, testimonials } from '../data/mock';

const HomePage = () => {
  const [currentHeroSlide, setCurrentHeroSlide] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHeroSlide((prev) => (prev + 1) % portfolioImages.hero.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const featuredProjects = portfolioProjects.slice(0, 4);

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative h-screen min-h-[700px] overflow-hidden">
        {/* Background Images */}
        {portfolioImages.hero.map((img, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              currentHeroSlide === index ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={img}
              alt="Interior Design"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#2C2C2C]/80 via-[#2C2C2C]/40 to-transparent" />
          </div>
        ))}

        {/* Hero Content */}
        <div className="relative z-10 h-full max-w-7xl mx-auto px-6 lg:px-8 flex items-center">
          <div className="max-w-2xl">
            <div className="animate-fade-in-up">
              <span className="inline-block px-4 py-1.5 bg-[#B87333]/20 border border-[#B87333]/40 text-[#B87333] text-xs tracking-[0.2em] uppercase mb-6">
                Interior Design Excellence
              </span>
            </div>
            <h1 className="animate-fade-in-up delay-200 font-serif text-4xl sm:text-5xl lg:text-6xl xl:text-7xl text-white leading-[1.1] mb-6">
              Designing interiors that{' '}
              <span className="text-[#D4956A]">inspire</span>, connect, and{' '}
              <span className="text-[#D4956A]">elevate</span> everyday living.
            </h1>
            <p className="animate-fade-in-up delay-300 text-white/80 text-lg md:text-xl mb-10 leading-relaxed max-w-xl">
              We create environments that transcend mere aesthetics — spaces that 
              tell your story and enhance your daily experience.
            </p>
            <div className="animate-fade-in-up delay-400 flex flex-wrap gap-4">
              <Link
                to="/gallery"
                className="inline-flex items-center px-8 py-4 bg-[#B87333] text-white text-sm tracking-wider uppercase hover:bg-[#8B5A2B] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-[#B87333]/30 group"
              >
                View Our Work
                <ArrowRight size={16} className="ml-3 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center px-8 py-4 border border-white/40 text-white text-sm tracking-wider uppercase hover:bg-white hover:text-[#2C2C2C] transition-all duration-300"
              >
                Start Your Project
              </Link>
            </div>
          </div>
        </div>

        {/* Slide Indicators */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 flex space-x-3">
          {portfolioImages.hero.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentHeroSlide(index)}
              className={`h-1 transition-all duration-500 ${
                currentHeroSlide === index
                  ? 'w-10 bg-[#B87333]'
                  : 'w-4 bg-white/40 hover:bg-white/60'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-10 right-10 z-20 hidden lg:block">
          <div className="flex flex-col items-center text-white/60">
            <span className="text-xs tracking-widest uppercase mb-3 [writing-mode:vertical-lr]">
              Scroll
            </span>
            <div className="w-px h-12 bg-white/30 relative overflow-hidden">
              <div className="absolute top-0 w-full h-1/2 bg-[#B87333] animate-bounce" />
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-24 lg:py-32 bg-[#FDFBF7] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-[#F5F0E8] -skew-x-12 origin-top-right" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block text-[#B87333] text-sm tracking-[0.2em] uppercase mb-4">
                Welcome to InArch
              </span>
              <h2 className="font-serif text-4xl lg:text-5xl text-[#2C2C2C] mb-6 leading-tight">
                Where Vision Meets
                <span className="block text-[#B87333]">Exceptional Craft</span>
              </h2>
              <div className="divider-copper mb-8" />
              <p className="text-[#6B6B6B] text-lg leading-relaxed mb-6">
                InArch Design Studio is a premier interior design firm based in Navi Mumbai, 
                dedicated to transforming spaces into extraordinary environments. With over 
                a decade of experience, we blend artistic vision with functional excellence.
              </p>
              <p className="text-[#6B6B6B] leading-relaxed mb-10">
                Our approach is deeply personal — we listen, understand, and then create 
                spaces that are uniquely yours. Every project is an opportunity to craft 
                an environment that inspires and elevates daily living.
              </p>
              <Link
                to="/about"
                className="inline-flex items-center text-[#B87333] text-sm tracking-wider uppercase hover:text-[#8B5A2B] transition-colors group"
              >
                Discover Our Story
                <ChevronRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="image-hover overflow-hidden">
                  <img
                    src={portfolioImages.livingRooms[0]}
                    alt="Living Room Design"
                    className="w-full h-64 object-cover"
                  />
                </div>
                <div className="image-hover overflow-hidden">
                  <img
                    src={portfolioImages.kitchens[0]}
                    alt="Kitchen Design"
                    className="w-full h-48 object-cover"
                  />
                </div>
              </div>
              <div className="pt-8 space-y-4">
                <div className="image-hover overflow-hidden">
                  <img
                    src={portfolioImages.bedrooms[0]}
                    alt="Bedroom Design"
                    className="w-full h-48 object-cover"
                  />
                </div>
                <div className="image-hover overflow-hidden">
                  <img
                    src={portfolioImages.offices[0]}
                    alt="Office Design"
                    className="w-full h-64 object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Highlights */}
      <section className="py-24 lg:py-32 bg-[#2C2C2C] relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-[#B87333] text-sm tracking-[0.2em] uppercase mb-4">
              What We Offer
            </span>
            <h2 className="font-serif text-4xl lg:text-5xl text-white mb-6">
              Our Services
            </h2>
            <p className="text-[#9A9A9A] max-w-2xl mx-auto">
              From concept to completion, we offer comprehensive interior design 
              services tailored to your unique vision and lifestyle.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: HomeIcon,
                title: 'Residential Interiors',
                description: 'Sophisticated living spaces that reflect your personality and enhance your daily life.',
                link: '/services#residential'
              },
              {
                icon: Building2,
                title: 'Commercial Interiors',
                description: 'Professional environments designed to inspire productivity and leave lasting impressions.',
                link: '/services#commercial'
              },
              {
                icon: Paintbrush,
                title: 'Landscape',
                description: 'Transform your outdoor spaces with beautiful gardens and sustainable landscaping.',
                link: '/services#landscape'
              },
              {
                icon: Building2,
                title: 'Villas & Resorts',
                description: 'Luxury interior design for exclusive villas, resorts, and hospitality spaces.',
                link: '/services#villas-resorts'
              }
            ].map((service, index) => (
              <Link
                key={index}
                to={service.link}
                className="group p-8 bg-[#3A3A3A]/50 border border-[#4A4A4A] hover:border-[#B87333] transition-all duration-500 hover:-translate-y-2"
              >
                <div className="w-14 h-14 flex items-center justify-center border border-[#B87333]/30 bg-[#B87333]/10 mb-6 group-hover:bg-[#B87333] group-hover:border-[#B87333] transition-all duration-300">
                  <service.icon size={24} className="text-[#B87333] group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-serif text-2xl text-white mb-4 group-hover:text-[#D4956A] transition-colors">
                  {service.title}
                </h3>
                <p className="text-[#9A9A9A] leading-relaxed mb-6">
                  {service.description}
                </p>
                <span className="inline-flex items-center text-[#B87333] text-sm tracking-wider uppercase opacity-0 group-hover:opacity-100 transition-opacity">
                  Learn More
                  <ArrowRight size={14} className="ml-2" />
                </span>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/services"
              className="inline-flex items-center px-8 py-4 border border-[#B87333] text-[#B87333] text-sm tracking-wider uppercase hover:bg-[#B87333] hover:text-white transition-all duration-300"
            >
              View All Services
              <ArrowRight size={16} className="ml-3" />
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-24 lg:py-32 bg-[#F5F0E8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16">
            <div>
              <span className="inline-block text-[#B87333] text-sm tracking-[0.2em] uppercase mb-4">
                Our Portfolio
              </span>
              <h2 className="font-serif text-4xl lg:text-5xl text-[#2C2C2C]">
                Featured Projects
              </h2>
            </div>
            <Link
              to="/gallery"
              className="mt-6 md:mt-0 inline-flex items-center text-[#B87333] text-sm tracking-wider uppercase hover:text-[#8B5A2B] transition-colors group"
            >
              View All Projects
              <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {featuredProjects.map((project, index) => (
              <Link
                key={project.id}
                to="/gallery"
                className="group relative overflow-hidden"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#2C2C2C]/90 via-[#2C2C2C]/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute bottom-0 left-0 right-0 p-8 translate-y-6 group-hover:translate-y-0 transition-transform duration-500">
                  <span className="inline-block px-3 py-1 bg-[#B87333]/20 text-[#B87333] text-xs tracking-wider uppercase mb-3 opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100">
                    {project.type}
                  </span>
                  <h3 className="font-serif text-2xl text-white mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-150">
                    {project.title}
                  </h3>
                  <p className="text-white/70 text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-200">
                    {project.description}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-[#B87333] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 border border-white/20 rounded-full -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-72 h-72 border border-white/20 rounded-full translate-x-1/2 translate-y-1/2" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="font-serif text-5xl lg:text-6xl text-white mb-2">
                  {stat.value}
                </div>
                <div className="text-white/80 text-sm tracking-wider uppercase">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 lg:py-32 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-[#B87333] text-sm tracking-[0.2em] uppercase mb-4">
              Client Stories
            </span>
            <h2 className="font-serif text-4xl lg:text-5xl text-[#2C2C2C] mb-6">
              What Our Clients Say
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="p-8 bg-white border border-[#E8E3DB] hover:border-[#B87333]/30 transition-colors duration-300"
              >
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={16} className="text-[#B87333] fill-[#B87333]" />
                  ))}
                </div>
                <p className="text-[#6B6B6B] leading-relaxed mb-6 italic">
                  "{testimonial.content}"
                </p>
                <div className="border-t border-[#E8E3DB] pt-4">
                  <div className="font-serif text-lg text-[#2C2C2C]">
                    {testimonial.name}
                  </div>
                  <div className="text-[#9A9A9A] text-sm">
                    {testimonial.role}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Instagram Section */}
      <section className="py-24 lg:py-32 bg-[#F5F0E8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block text-[#B87333] text-sm tracking-[0.2em] uppercase mb-4">
              Follow Us
            </span>
            <h2 className="font-serif text-4xl lg:text-5xl text-[#2C2C2C] mb-4">
              @inarch_design_studio_belapur
            </h2>
            <p className="text-[#6B6B6B]">
              Get inspired by our latest projects and design ideas
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[...portfolioImages.livingRooms, portfolioImages.bedrooms[0]].map((img, index) => (
              <a
                key={index}
                href="https://instagram.com/inarch_design_studio_belapur"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative aspect-square overflow-hidden"
              >
                <img
                  src={img}
                  alt="Instagram Post"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-[#B87333]/0 group-hover:bg-[#B87333]/40 transition-all duration-300 flex items-center justify-center">
                  <Play size={32} className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 lg:py-32 bg-[#2C2C2C] relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full">
          <img
            src={portfolioImages.hero[2]}
            alt="Background"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center relative">
          <span className="inline-block text-[#B87333] text-sm tracking-[0.2em] uppercase mb-4">
            Start Your Journey
          </span>
          <h2 className="font-serif text-4xl lg:text-5xl xl:text-6xl text-white mb-6 leading-tight">
            Ready to Transform Your Space?
          </h2>
          <p className="text-white/70 text-lg mb-10 max-w-2xl mx-auto">
            Let's design something extraordinary together. Your dream space is 
            just a conversation away.
          </p>
          <a
            href="https://wa.me/918591613610"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-10 py-5 bg-[#B87333] text-white text-sm tracking-wider uppercase hover:bg-[#8B5A2B] transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-[#B87333]/30 group"
          >
            Get in Touch
            <ArrowRight size={16} className="ml-3 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </section>
    </Layout>
  );
};

export default HomePage;
